import { CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser'

/**
 * RUM tracking utilities for user journey analytics
 * All tracking functions are no-ops when VITE_CORALOGIX_ENABLED is not 'true'
 */

const isEnabled = import.meta.env.VITE_CORALOGIX_ENABLED === 'true'

// Event categories for consistent tracking
export const RumCategory = {
  ACCOUNT: 'account',
  FEEDBACK: 'feedback',
  BUILDER: 'builder',
  NAVIGATION: 'navigation',
  AUTH: 'auth',
} as const

type RumCategoryType = (typeof RumCategory)[keyof typeof RumCategory]

interface TrackEventOptions {
  category: RumCategoryType
  action: string
  data?: Record<string, unknown>
  labels?: Record<string, string>
}

/**
 * Track a user action/event
 */
export function trackEvent({ category, action, data, labels }: TrackEventOptions) {
  if (!isEnabled) return
  const eventName = `${category}.${action}`
  CoralogixRum.log(CoralogixLogSeverity.Info, eventName, data ?? null, labels)
}

/**
 * Track successful operations
 */
export function trackSuccess(category: RumCategoryType, action: string, data?: Record<string, unknown>) {
  trackEvent({
    category,
    action: `${action}.success`,
    data,
    labels: { status: 'success' },
  })
}

/**
 * Track failed operations
 */
export function trackError(category: RumCategoryType, action: string, error: unknown, data?: Record<string, unknown>) {
  if (!isEnabled) return
  const errorMessage = error instanceof Error ? error.message : String(error)
  CoralogixRum.log(
    CoralogixLogSeverity.Error,
    `${category}.${action}.error`,
    { ...data, error: errorMessage },
    { status: 'error' }
  )
}

// Account tracking
export const trackAccount = {
  createStarted: () => trackEvent({ category: RumCategory.ACCOUNT, action: 'create.started' }),
  createSuccess: (accountToken: string, productCode: string) =>
    trackSuccess(RumCategory.ACCOUNT, 'create', { accountToken, productCode }),
  createError: (error: unknown) => trackError(RumCategory.ACCOUNT, 'create', error),

  updateStarted: (accountToken: string) =>
    trackEvent({ category: RumCategory.ACCOUNT, action: 'update.started', data: { accountToken } }),
  updateSuccess: (accountToken: string) => trackSuccess(RumCategory.ACCOUNT, 'update', { accountToken }),
  updateError: (accountToken: string, error: unknown) =>
    trackError(RumCategory.ACCOUNT, 'update', error, { accountToken }),

  viewed: (accountToken: string) =>
    trackEvent({ category: RumCategory.ACCOUNT, action: 'viewed', data: { accountToken } }),
  listViewed: (count: number) =>
    trackEvent({ category: RumCategory.ACCOUNT, action: 'list.viewed', data: { count } }),
}

// Feedback tracking
export const trackFeedback = {
  listViewed: (accountToken: string, count: number) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'list.viewed', data: { accountToken, count } }),
  itemViewed: (feedbackId: string, vote?: string) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'item.viewed', data: { feedbackId, vote } }),
  filtered: (filters: Record<string, unknown>) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'filtered', data: filters }),
  accountSelected: (accountToken: string) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'account.selected', data: { accountToken } }),
  analyticsViewed: (accountToken: string) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'analytics.viewed', data: { accountToken } }),
  exported: (format: string, count: number) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'exported', data: { format, count } }),
  featureDetailViewed: (accountToken: string, feature: string) =>
    trackEvent({ category: RumCategory.FEEDBACK, action: 'feature_detail.viewed', data: { accountToken, feature } }),
}

// Builder tracking
export const trackBuilder = {
  presetSelected: (preset: string) =>
    trackEvent({ category: RumCategory.BUILDER, action: 'preset.selected', data: { preset } }),
  configChanged: (field: string, value: unknown) =>
    trackEvent({ category: RumCategory.BUILDER, action: 'config.changed', data: { field, value } }),
  codeCopied: (environment: string) =>
    trackEvent({ category: RumCategory.BUILDER, action: 'code.copied', data: { environment } }),
  environmentChanged: (environment: string) =>
    trackEvent({ category: RumCategory.BUILDER, action: 'environment.changed', data: { environment } }),
}

// Navigation/page view tracking
export const trackNavigation = {
  pageViewed: (page: string, params?: Record<string, string>) =>
    trackEvent({ category: RumCategory.NAVIGATION, action: 'page.viewed', data: { page, ...params } }),
}

// Auth tracking
export const trackAuth = {
  loginSuccess: (userId: string) =>
    trackSuccess(RumCategory.AUTH, 'login', { userId }),
  loginError: (error: unknown) => trackError(RumCategory.AUTH, 'login', error),
  logout: () => trackEvent({ category: RumCategory.AUTH, action: 'logout' }),
}
